import { jsPDF } from "jspdf";

/** Match quote PDF palette */
const BLUE_PRIMARY = [41, 119, 237];
const BLUE_HEADER_RIGHT = [30, 90, 200];
/** Match {@link generateQuotePdf} wordmark */
const DIGI_CYAN = [0, 163, 218];
const XLEADS_GRAY = [58, 76, 93];
const WATERMARK_RGB = [200, 242, 250];

const sanitize = (value) => {
  if (value == null) return "";
  return String(value).replace(/\r?\n/g, " ").trim();
};

const htmlToNoteLines = (html) => {
  const value = String(html || "").trim();
  if (!value) return [];
  const root = document.createElement("div");
  root.innerHTML = value;
  const lines = [[{ text: "", bold: false }]];
  let currentLineIndex = 0;

  const normalizeInlineText = (txt) => String(txt || "").replace(/\u00A0/g, " ").replace(/\s+/g, " ");
  const ensureLine = () => {
    if (lines[currentLineIndex] == null) lines[currentLineIndex] = [];
  };
  const appendText = (txt, bold = false) => {
    const cleaned = normalizeInlineText(txt);
    if (!cleaned.trim()) return;
    ensureLine();
    const line = lines[currentLineIndex];
    const last = line[line.length - 1];
    if (last && last.bold === bold) {
      last.text += cleaned;
    } else {
      line.push({ text: cleaned, bold });
    }
  };
  const newLine = () => {
    if (lines[currentLineIndex] == null) lines[currentLineIndex] = [];
    currentLineIndex += 1;
    lines[currentLineIndex] = [];
  };
  const trimRightCurrent = (keepLeading = false) => {
    ensureLine();
    const line = lines[currentLineIndex];
    if (!line.length) return;
    line[line.length - 1].text = line[line.length - 1].text.replace(/\s+$/g, "");
    if (!keepLeading && line[0]) line[0].text = line[0].text.replace(/^\s+/g, "");
  };

  const BLOCK_TAGS = new Set([
    "p",
    "div",
    "section",
    "article",
    "blockquote",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6"
  ]);

  const walk = (node, bold = false) => {
    if (!node) return;
    if (node.nodeType === Node.TEXT_NODE) {
      appendText(node.textContent || "", bold);
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tag = node.tagName.toLowerCase();
    const fwRaw = String(node.style?.fontWeight || node.getAttribute?.("data-font-weight") || "").toLowerCase();
    const fwNum = Number(fwRaw);
    const styleBold = fwRaw === "bold" || fwRaw === "bolder" || (!Number.isNaN(fwNum) && fwNum >= 600);
    const isBold = bold || tag === "strong" || tag === "b" || styleBold;
    if (tag === "br") {
      trimRightCurrent();
      newLine();
      return;
    }

    if (tag === "ul" || tag === "ol") {
      const items = Array.from(node.children).filter((child) => child.tagName?.toLowerCase() === "li");
      items.forEach((li, idx) => {
        trimRightCurrent();
        const marker = tag === "ol" ? `${idx + 1}. ` : "• ";
        appendText(marker, false);
        Array.from(li.childNodes).forEach((child) => walk(child, isBold));
        trimRightCurrent();
        newLine();
      });
      return;
    }

    const isBlock = BLOCK_TAGS.has(tag);
    if (isBlock && lines[currentLineIndex].some((seg) => String(seg.text || "").trim().length > 0)) {
      trimRightCurrent(true);
      newLine();
    }
    Array.from(node.childNodes).forEach((child) => walk(child, isBold));
    if (isBlock) {
      trimRightCurrent();
      newLine();
    }
  };

  Array.from(root.childNodes).forEach((node) => walk(node));
  return lines
    .map((line) => {
      const next = line
        .map((seg) => ({ ...seg, text: seg.text }))
        .filter((seg) => seg.text && seg.text.trim().length > 0);
      return next;
    })
    .filter((line, idx, arr) => line.length > 0 || (idx > 0 && arr[idx - 1]?.length > 0));
};

/** Issuer contact on receipt (DigixLeads / Tumma — not customer `company` from the form) */
const DIGIXLEADS_ISSUER_LINES = [
  "Hyderabad, Telangana, 500079",
  "+91 (814) 225 - 1234",
  "info@tumma.co"
];


const drawWatermark = (doc) => {
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const cx = pageWidth / 2;
  const cy = pageHeight / 2;
  doc.setFontSize(60);
  doc.setTextColor(...WATERMARK_RGB);
  doc.setFont("helvetica", "normal");
  doc.text("DIGIXLEADS", cx + 15, cy, { align: "center", baseline: "middle", angle: 45 });
};

/** Indian number grouping — ASCII only for jsPDF standard fonts */
const formatIndianNumber = (value) => {
  const n = Math.floor(Math.abs(Number(value || 0)));
  const s = String(n);
  if (s.length <= 3) return s;
  const last3 = s.slice(-3);
  let rest = s.slice(0, -3);
  const parts = [];
  while (rest.length > 2) {
    parts.unshift(rest.slice(-2));
    rest = rest.slice(0, -2);
  }
  if (rest) parts.unshift(rest);
  return `${parts.join(",")},${last3}`;
};

const numberToWords = (num) => {
  const n = Math.floor(Number(num || 0));
  if (n === 0) return "ZERO";

  const ones = [
    "",
    "ONE",
    "TWO",
    "THREE",
    "FOUR",
    "FIVE",
    "SIX",
    "SEVEN",
    "EIGHT",
    "NINE",
    "TEN",
    "ELEVEN",
    "TWELVE",
    "THIRTEEN",
    "FOURTEEN",
    "FIFTEEN",
    "SIXTEEN",
    "SEVENTEEN",
    "EIGHTEEN",
    "NINETEEN"
  ];
  const tens = ["", "", "TWENTY", "THIRTY", "FORTY", "FIFTY", "SIXTY", "SEVENTY", "EIGHTY", "NINETY"];

  const twoDigits = (x) => {
    if (x < 20) return ones[x];
    return `${tens[Math.floor(x / 10)]}${x % 10 ? ` ${ones[x % 10]}` : ""}`;
  };

  const threeDigits = (x) => {
    const hundred = Math.floor(x / 100);
    const rem = x % 100;
    const h = hundred ? `${ones[hundred]} HUNDRED` : "";
    const r = rem ? twoDigits(rem) : "";
    return [h, r].filter(Boolean).join(" ");
  };

  const crore = Math.floor(n / 10000000);
  const lakh = Math.floor((n % 10000000) / 100000);
  const thousand = Math.floor((n % 100000) / 1000);
  const rest = n % 1000;

  const parts = [];
  if (crore) parts.push(`${threeDigits(crore)} CRORE`);
  if (lakh) parts.push(`${threeDigits(lakh)} LAKH`);
  if (thousand) parts.push(`${threeDigits(thousand)} THOUSAND`);
  if (rest) parts.push(threeDigits(rest));
  return parts.join(" ").trim();
};

/**
 * Receipt PDF — visual structure aligned with {@link generateQuotePdf} (colors, watermark, blue table).
 */
export function generateReceiptPdf(data) {
  const { invoice = {}, billedTo = {}, lineItems = [] } = data || {};

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const tableMargin = 20;
  const tableRight = pageWidth - tableMargin;

  drawWatermark(doc);

  const defaultTextStyle = () => {
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(0, 0, 0);
  };
  const headingStyle = () => {
    doc.setFontSize(11);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
  };

  let yPos = 20;
  defaultTextStyle();

  doc.setFontSize(14);
  doc.setFont("helvetica");
  doc.setTextColor(0, 0, 0);
  doc.text("RECEIPT", tableMargin, yPos);
  defaultTextStyle();
  const invoiceDate = sanitize(invoice.invoiceDate || new Date().toISOString().split("T")[0]);
  doc.text(`Date: ${invoiceDate}`, tableRight, yPos, { align: "right" });
  yPos += 8;
  doc.setLineWidth(0.5);
  doc.setDrawColor(...BLUE_PRIMARY);
  doc.line(tableMargin, yPos, tableRight, yPos);
  yPos += 10;

  /** Issuer (DIGI / XLEADS wordmark + fixed contact) left column; client details right */
  const issuerStartY = yPos;
  let leftY = issuerStartY;
  let rightY = issuerStartY - 2;

  doc.setFontSize(22);
  doc.setFont("helvetica", "bold");
  const digiPart = "Digi";
  const xleadsPart = "XLeads";
  doc.setTextColor(...DIGI_CYAN);
  doc.text(digiPart, tableMargin, leftY);
  doc.setTextColor(...XLEADS_GRAY);
  doc.text(xleadsPart, tableMargin + doc.getTextWidth(digiPart), leftY);
  leftY += 9;

  defaultTextStyle();
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  DIGIXLEADS_ISSUER_LINES.forEach((line) => {
    doc.text(line, tableMargin, leftY);
    leftY += 5;
  });

  defaultTextStyle();
  const clientCompany = sanitize(billedTo.companyName || billedTo.company || "");
  const billedName = sanitize(billedTo.name || "");
  if (billedName) {
    doc.text(billedName, tableRight, rightY, { align: "right" });
    rightY += 5;
  }
  if (clientCompany) {
    doc.text(clientCompany, tableRight, rightY, { align: "right" });
    rightY += 5;
  }
  if (!billedName && !clientCompany) {
    doc.text("Customer", tableRight, rightY, { align: "right" });
    rightY += 5;
  }
  const billedEmail = sanitize(billedTo.email || "");
  if (billedEmail) {
    doc.text(billedEmail, tableRight, rightY, { align: "right" });
    rightY += 5;
  }
  const billedPhone = sanitize(billedTo.phone || billedTo.mobile || "");
  if (billedPhone) {
    doc.text(billedPhone, tableRight, rightY, { align: "right" });
    rightY += 5;
  }
  const billedAddress = sanitize(billedTo.address || "");
  if (billedAddress) {
    const addrLines = doc.splitTextToSize(billedAddress, pageWidth - 60);
    doc.text(addrLines, tableRight, rightY, { align: "right" });
    rightY += addrLines.length * 5;
  }
  yPos = Math.max(leftY, rightY) + 6;

  /* —— Line items table (quote-style two-tone header) —— */
  const headerWidth = pageWidth - tableMargin * 2;
  const leftWidth = headerWidth * 0.52;
  const rightWidth = headerWidth * 0.48;
  const headerHeight = 7;
  const colServicesX = tableMargin + 8;
  const colRightStart = tableMargin + leftWidth;
  const rowPadding = 2;

  /** Right edges (mm) for Invoice amount, Due amount, Paid amount */
  const numPad = 4;
  const numLeft = colRightStart + numPad;
  const numRight = tableRight - numPad;
  const numW = Math.max(numRight - numLeft, 1);
  const numColFracs = [0.34, 0.33, 0.33];
  const numHeaderLabels = ["Invoice amount", "Due amount", "Paid amount"];
  let numX = numLeft;
  const numericColRights = numColFracs.map((frac) => {
    numX += numW * frac;
    return numX - 1;
  });

  const drawTableHeader = (headerY) => {
    doc.setFillColor(...BLUE_PRIMARY);
    doc.rect(tableMargin, headerY, leftWidth, headerHeight, "F");
    doc.setFillColor(...BLUE_HEADER_RIGHT);
    doc.rect(colRightStart, headerY, rightWidth, headerHeight, "F");
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text("Service", colServicesX, headerY + headerHeight / 2 + 1.5);
    numHeaderLabels.forEach((label, i) => {
      doc.text(label, numericColRights[i], headerY + headerHeight / 2 + 1.5, { align: "right" });
    });
  };

  drawTableHeader(yPos);
  yPos += headerHeight + 4;

  defaultTextStyle();
  const servicesColumnWidth = leftWidth - 16;

  const rows =
    Array.isArray(lineItems) && lineItems.length > 0
      ? lineItems
      : [{ item: "Service", quantity: 1, rate: 0, advances: 0 }];

  let grandTotal = 0;
  let grandPaid = 0;

  const drawNumericCells = (baseY, values, opts = {}) => {
    doc.setFontSize(10);
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setTextColor(0, 0, 0);
    values.forEach((val, i) => {
      doc.text(String(val), numericColRights[i], baseY, { align: "right" });
    });
  };

  rows.forEach((row, idx) => {
    if (yPos > pageHeight - 40) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc);
      drawTableHeader(yPos);
      yPos += headerHeight + 4;
      defaultTextStyle();
    }

    defaultTextStyle();

    const qty = Number(row.quantity || 0);
    const rate = Number(row.rate || 0);
    const advances = Number(row.advances || 0);
    const amount = qty * rate;
    grandTotal += amount;
    grandPaid += advances;
    const due = Number(row.dueAmount != null ? row.dueAmount : amount - advances);

    const itemText = sanitize(row.item || "Service");
    const serviceLines = doc.splitTextToSize(itemText, servicesColumnWidth);
    const leftHeight = Array.isArray(serviceLines) ? serviceLines.length * 5 : 5;
    const rowHeight = Math.max(leftHeight, 8) + rowPadding * 2;
    const textBaseY = yPos + rowPadding + 2;

    doc.text(serviceLines, colServicesX, textBaseY);
    drawNumericCells(textBaseY, [
      formatIndianNumber(amount),
      formatIndianNumber(due),
      formatIndianNumber(advances)
    ]);

    yPos += rowHeight;

    if (idx < rows.length - 1) {
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(0.2);
      doc.line(tableMargin, yPos, tableRight, yPos);
      yPos += 2;
    }
  });

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.5);
  doc.line(tableMargin, yPos, tableRight, yPos);
  yPos += 6;

  defaultTextStyle();
  doc.setFont("helvetica", "bold");
  doc.text("Total", colServicesX, yPos);
  /* Show total paid amount in Paid amount column */
  doc.setFontSize(10);
  doc.setTextColor(0, 0, 0);
  doc.text(formatIndianNumber(grandPaid), numericColRights[2], yPos, { align: "right" });
  yPos += 10;

  if (yPos > pageHeight - 50) {
    doc.addPage();
    yPos = 20;
    drawWatermark(doc);
    defaultTextStyle();
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("Total In Words:", tableMargin, yPos);
  doc.setTextColor(...BLUE_PRIMARY);
  doc.setFont("helvetica", "bold");
  const wordsLine = `${numberToWords(grandPaid)} RUPEES ONLY`;
  /* Amount in words on the right, wrapped within right column */
  const labelReserveMm = 48;
  const wordsMaxW = Math.max(tableRight - tableMargin - labelReserveMm, 55);
  const wordsWrapped = doc.splitTextToSize(wordsLine, wordsMaxW);
  let wordsY = yPos;
  wordsWrapped.forEach((line) => {
    doc.text(line, tableRight, wordsY, { align: "right" });
    wordsY += 5;
  });
  yPos = wordsY + 10;

  const noteLines = htmlToNoteLines(invoice.notes);
  const footerTopSafe = pageHeight - 26;

  if (noteLines.length) {
    const drawNotesContent = () => {
      headingStyle();
      doc.text("Notes:", tableMargin, yPos);
      yPos += 6;
      defaultTextStyle();
      const drawStyledLine = (segments) => {
        let x = tableMargin;
        const maxX = tableRight;
        const tokens = [];
        segments.forEach((seg) => {
          String(seg.text)
            .split(/(\s+)/)
            .filter((t) => t.length > 0)
            .forEach((t) => tokens.push({ text: t, bold: seg.bold }));
        });
        if (!tokens.length) {
          yPos += 3;
          return;
        }
        tokens.forEach((token) => {
          const isWhitespace = /^\s+$/.test(token.text);
          doc.setFont("helvetica", token.bold ? "bold" : "normal");
          const tokenWidth = doc.getTextWidth(token.text);
          if (!isWhitespace && x > tableMargin && x + tokenWidth > maxX) {
            yPos += 5;
            x = tableMargin;
          }
          if (yPos > footerTopSafe) {
            doc.addPage();
            yPos = 20;
            drawWatermark(doc);
            defaultTextStyle();
            x = tableMargin;
          }
          doc.text(token.text, x, yPos);
          x += tokenWidth;
        });
        yPos += 5;
      };
      noteLines.forEach((segments) => {
        if (!segments.length) {
          yPos += 3;
          return;
        }
        drawStyledLine(segments);
      });
    };

    if (yPos > footerTopSafe - 15) {
      doc.addPage();
      yPos = 20;
      drawWatermark(doc);
    }
    drawNotesContent();
  }

  

  return doc;
}
